<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("ofertas.grid.titulo") }}</h1>
      <div class="toolbar">
        <button type="button" @click.prevent="crearOferta()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid v-bind="gridProperties" :data-source="gridSource" ref="grid">
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="Nombre" :caption="$t('ofertas.grid.nombre')"></DxColumn>
      <DxColumn data-field="FechaInicio" :caption="$t('ofertas.grid.fecha_inicio')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="FechaFin" :caption="$t('ofertas.grid.fecha_fin')" data-type="date" format="dd/MM/yyyy"> </DxColumn>
      <DxColumn data-field="TipoEnergia" :caption="$t('ofertas.grid.tipo_energia')">
        <DxLookup v-bind="getLookupTipoEnergia()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="TarifaAcceso" :caption="$t('ofertas.grid.tarifa_acceso')"></DxColumn>
      <DxColumn data-field="Autoconsumo" :caption="$t('ofertas.grid.autoconsumo')" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="PotenciaMin" :caption="$t('ofertas.grid.potencia_min')" data-type="number" cell-template="potenciaTemplate"></DxColumn>
      <DxColumn data-field="PotenciaMax" :caption="$t('ofertas.grid.potencia_max')" data-type="number" cell-template="potenciaTemplate"></DxColumn>
      <DxColumn data-field="AplicaPersonaFisica" :caption="$t('ofertas.grid.aplica_fisica')" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="AplicaPersonaJuridica" :caption="$t('ofertas.grid.aplica_juridica')" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Activa" :caption="$t('ofertas.grid.estado')" cell-template="estadoTemplate">
        <DxLookup v-bind="getLookupEstadoActivacion()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="FechaModificacion" data-type="datetime" format="dd/MM/yyyy HH:mm:ss" :caption="$t('ofertas.grid.fechaModificacion')"> </DxColumn>
      <DxColumn data-field="FechaUltSincronizacion" data-type="datetime" format="dd/MM/yyyy HH:mm:ss" :caption="$t('ofertas.grid.fechaUltSincronizacion')">
      </DxColumn>
      <DxColumn
        type="buttons"
        :caption="$t('empresas_de_servicio.grid.acciones')"
        :fixed="true"
        fixed-position="right"
        :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('ofertas.grid.duplicar'),
            icon: 'fa-solid fa-clone text-success',
            onClick: (e: any) => duplicar(e.row?.data.Id),
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('ofertas.grid.modificar'),
            icon: 'fa-regular fa-edit text-success',
            onClick: (e: any) => verDetalle(e.row?.data.Id),
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('ofertas.grid.borrar'),
            icon: 'fa-regular fa-trash text-success',
            onClick: (e: any) => borrar(e.row?.data.Id),
            disabled: (options: any) => options.row?.data.TieneTramitesAsociados,
          },
        ]"
      />

      <template #estadoTemplate="{ data }">
        <div :class="[`d-inline-block rounded py-1 px-2`, `estado ${data.value ? 'activado' : 'desactivado'}`]">
          {{ $t(`ofertas.estado.${data.value ? "activado" : "desactivado"}`) }}
        </div>
      </template>
      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>
      <template #potenciaTemplate="{ data }">
        <span v-if="data.value">{{ $n(data.value) }} kW</span>
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { DxDataGrid, DxColumn, DxLookup, DxColumnFixing } from "devextreme-vue/data-grid";
import { getDefaultGridProperties, getLookup, getColumnLookupBoolean } from "@helpers/grid-helper";
import { useApi } from "@api/integration";
import type CustomStore from "devextreme/data/custom_store";
import { useIntl } from "@services/i18n";
import type { ColumnLookup } from "devextreme/ui/data_grid";
import { type DXLoadOptions } from "@/.shared/.api";
import { useModalService } from "@services/modal-service";
import { messageBoxResult } from "@components/dialogs/message-box";
import { CreateOfertaEnergia } from "@/components/ofertas-energia/base/models/CreateOfertaEnergia";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import { Constants } from "@/.shared/constants/OperacionEnergia";
import type { Component } from "vue";
import { CreateOfertaEndesa } from "@/components/ofertas-energia/endesa/models/CreateOfertaEndesa";
import OfertaEnergiaTabPanel from "@/components/ofertas-energia/base/OfertaEnergiaTabPanel.vue";
import OfertaEndesaTabPanel from "@/components/ofertas-energia/endesa/OfertaEndesaTabPanel.vue";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";
import { modalMaxHeight, xlModalPopupProps } from "@/.shared/helpers/modal-helper";

const intl = useIntl();
const api = useApi();
const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const modal = useModalService();
const gridProperties = ref<DxDataGrid>();

const props = defineProps({
  idEmpresaServicios: String,
});

const defaultPopUpProps = { ...xlModalPopupProps, minHeight: modalMaxHeight };

onMounted(async () => {
  setDatasource();
});

function getLookupEstadoActivacion(): ColumnLookup {
  return {
    dataSource: [
      {
        value: true,
        text: intl.i18n.t(`ofertas.estado.activado`),
      },
      {
        value: false,
        text: intl.i18n.t(`ofertas.estado.desactivado`),
      },
    ],
    valueExpr: "value",
    displayExpr: "text",
  };
}

function getLookupTipoEnergia(): ColumnLookup {
  return getLookup("ofertas", "tipo_energia");
}

async function crearOferta() {
  const title = `${intl.i18n.t("ofertas.creacion.titulo")}`;
  var detalleEmpresaServicios = await api.callStandardApi("getEmpresaServiciosEnergiaById", { showLoading: false }, props.idEmpresaServicios!);
  let nombreEmpresaServicios = detalleEmpresaServicios.Nombre;

  let modalComponent: Component;
  let modalProps: Record<string, unknown>;

  if (Constants.EmpresaServicios.EMPRESAS_ENDESA.includes(props.idEmpresaServicios!)) {
    modalComponent = OfertaEndesaTabPanel;
    modalProps = {
      model: new CreateOfertaEndesa({ IdEmpresaServicios: props.idEmpresaServicios, EmpresaServicios: nombreEmpresaServicios }),
      isEditMode: false,
      tieneTramitesAsociados: false,
    };
  } else {
    modalComponent = OfertaEnergiaTabPanel;
    modalProps = {
      model: new CreateOfertaEnergia({ IdEmpresaServicios: props.idEmpresaServicios, EmpresaServicios: nombreEmpresaServicios }),
      isEditMode: false,
      tieneTramitesAsociados: false,
    };
  }

  modal.showModal(modalComponent, modalProps, { ...defaultPopUpProps, title: title }).then((value) => {
    if (value == messageBoxResult.ok) grid.value?.instance?.refresh();
  });
}

const setDatasource = async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;
      if (q.filter != null && q.filter.length >= 0) q.filter = [["IdEmpresaServicios", "=", props.idEmpresaServicios], "and", q.filter];
      else q.filter = ["IdEmpresaServicios", "=", props.idEmpresaServicios];

      return await api.callStandardApi("getOfertasEnergiaForGrid", { showLoading: false }, q);
    },
  });
};

const duplicar = async (idOferta: string) => {
  await api
    .callStandardApi("duplicateOfertaEnergia", { showLoading: true }, idOferta)
    .then(() => {
      toastSuccess(intl.i18n.t("oferta_energia.duplicacion_exito"));
      grid.value?.instance?.refresh();
    })
    .catch(() => toastError(intl.i18n.t("oferta_energia.duplicacion_fallo")));
};

const verDetalle = async (idOfertaEnergia: string) => {
  let title = `${intl.i18n.t("oferta_energia.edicion.titulo")}`;

  let modalComponent: Component;
  let modalProps: Record<string, unknown>;

  if (Constants.EmpresaServicios.EMPRESAS_ENDESA.includes(props.idEmpresaServicios!)) {
    let value = await api.callStandardApi('getOfertaEndesaById', { showLoading: true }, idOfertaEnergia);

    modalComponent = OfertaEndesaTabPanel;
    modalProps = {
      model: new CreateOfertaEndesa().createFromOfertaDetail(value),
      isEditMode: true,
      tieneTramitesAsociados: value.TieneTramitesAsociados,
    };
  } else {
    let value = await api.callStandardApi("getOfertaEnergiaById", { showLoading: true }, idOfertaEnergia);

    modalComponent = OfertaEnergiaTabPanel;
    modalProps = { model: new CreateOfertaEnergia().createFromOfertaDetail(value), isEditMode: true, tieneTramitesAsociados: value.TieneTramitesAsociados };
  }

  let response = await modal.showModal(modalComponent, modalProps, { title: title, ...defaultPopUpProps });

  if (response == messageBoxResult.ok) grid.value?.instance?.refresh();
};

const borrar = async (idOferta: string) => {
  let respuesta = await modal.messageBox(
    intl.i18n.t("ofertas.grid.borrar_pregunta"),
    intl.i18n.t("ofertas.grid.titulo_confirma_borrar"),
    "okCancel",
    "warning",
  );
  if (respuesta == messageBoxResult.ok)
    await api
      .callStandardApi("deleteOfertaEnergia", { showLoading: true }, idOferta)
      .then(() => {
        toastSuccess(intl.i18n.t("oferta_energia.borrado_exito"));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t("oferta_energia.borrado_fallo")));
};

onMounted(() => {
  gridProperties.value = getDefaultGridProperties();
});
</script>

<style lang="scss" scoped>



.icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 20px;
}
</style>
